/**
 * Toast notification utility
 * Dispatches custom events for the Toast component
 */

export const ToastType = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
};

/**
 * Show a toast notification
 * @param {string} message - The message to display
 * @param {object} options - Optional settings
 * @param {string} options.type - Toast type: 'info' | 'success' | 'warning' | 'error'
 * @param {number} options.duration - Duration in ms (default varies by type)
 * @param {number} options.retryAfter - Retry-after seconds (for rate limit errors)
 */
export function showToast(message, options = {}) {
  const { type = 'info', duration, retryAfter } = options;

  window.dispatchEvent(
    new CustomEvent('app-toast', {
      detail: {
        message,
        type,
        duration,
        retryAfter,
      },
    })
  );
}

/**
 * Convenience methods for each toast type
 */
export const toast = {
  info: (message, options = {}) => showToast(message, { ...options, type: 'info' }),
  success: (message, options = {}) => showToast(message, { ...options, type: 'success' }),
  warning: (message, options = {}) => showToast(message, { ...options, type: 'warning' }),
  error: (message, options = {}) => showToast(message, { ...options, type: 'error' }),

  /**
   * Show a rate limit error with retry-after time
   * @param {number|null} retryAfter - Seconds until retry is allowed (optional)
   */
  rateLimit: (retryAfter = null, limitScope = 'app_rate_limit') => {
    const message = limitScope === 'ai_provider_quota'
      ? 'AI quota/rate limit reached. Please wait before retrying AI actions.'
      : 'API request limit reached. Please wait before trying again.';
    showToast(message, {
      type: 'error',
      duration: 5000,
      retryAfter,
    });
  },
};

export default toast;
